import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Toolbar,
  AppBar,
} from '@mui/material';
import { Radio, Logout } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { figma } from '../theme/figmaTokens';
import { formatStationMenuLabel } from '../utils/stationDisplay';

const onBar = {
  label: 'rgba(255,255,255,0.92)',
  icon: '#999999',
  border: 'rgba(255,255,255,0.25)',
  borderHover: 'rgba(255,255,255,0.4)',
};

const topBarIcon = '#999999';

interface Station {
  id: number;
  stationName: string;
  callLetters: string;
  market: string;
}

interface StationSelectionProps {
  onStationSelected: (station: Station) => void;
}

const StationSelection: React.FC<StationSelectionProps> = ({ onStationSelected }) => {
  const [stations, setStations] = useState<Station[]>([]);
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    const fetchStations = async () => {
      try {
        setError('');
        setLoading(true);
        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login', { replace: true });
          return;
        }

        const stationsData = await apiService.getUserStations(token);
        const list = Array.isArray(stationsData) ? stationsData : [];
        setStations(list);
        localStorage.setItem('userStations', JSON.stringify(list));

        let initialId: number | '' = '';
        const lastId = user?.lastSelectedStation;
        if (lastId != null && list.some((s) => s.id === Number(lastId))) {
          initialId = Number(lastId);
        } else if (list.length === 1) {
          initialId = list[0].id;
        }
        setSelectedId(initialId);
      } catch (err: any) {
        console.error('Error fetching stations:', err);
        const status = err.response?.status ?? err.status;
        if (status === 401) {
          setError(
            'Could not load stations with your session (unauthorized). Try again, or log out and sign in again.'
          );
        } else {
          setError(err.message || 'Failed to load stations');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStations();
  }, [navigate, user?.lastSelectedStation]);

  const handleRetry = () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    apiService
      .getUserStations(token)
      .then((stationsData) => {
        const list = Array.isArray(stationsData) ? stationsData : [];
        setStations(list);
        localStorage.setItem('userStations', JSON.stringify(list));
        let initialId: number | '' = '';
        const lastId = user?.lastSelectedStation;
        if (lastId != null && list.some((s) => s.id === Number(lastId))) {
          initialId = Number(lastId);
        } else if (list.length === 1) {
          initialId = list[0].id;
        }
        setSelectedId(initialId);
      })
      .catch((err: any) => {
        const status = err.response?.status ?? err.status;
        setError(
          status === 401
            ? 'Could not load stations with your session (unauthorized). Try again, or log out and sign in again.'
            : err.message || 'Failed to load stations'
        );
      })
      .finally(() => setLoading(false));
  };

  const handleContinue = () => {
    const station = stations.find((s) => s.id === selectedId);
    if (!station) return;

    localStorage.setItem('selectedStation', JSON.stringify(station));
    onStationSelected(station);
    navigate('/dashboard');
  };

  const centerMessage =
    stations.length === 0
      ? 'No stations are available for your account.'
      : 'Choose a station in the dropdown above, then select Continue to launch the HotQuus Dashboard';

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: figma.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: figma.cyan }} size={40} />
          <Typography variant="h6" sx={{ mt: 2, color: figma.textMuted }}>
            Loading stations...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: figma.pageBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
            {error}
          </Alert>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleRetry}
              sx={{
                textTransform: 'none',
                color: '#ffffff',
                backgroundColor: figma.cyan,
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#2d8fc4', boxShadow: 'none' },
              }}
            >
              Retry
            </Button>
            <Button
              variant="outlined"
              onClick={handleLogout}
              sx={{ textTransform: 'none', color: figma.textMuted, borderColor: figma.border }}
            >
              Log out
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: figma.pageBg, display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" elevation={0} sx={{ backgroundColor: '#000000', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <Toolbar sx={{ gap: 1.5, py: 1, minHeight: 64 }}>
          <Box component="img" src={`${process.env.PUBLIC_URL}/Quu_Logo_Gray.png`} alt="Quu" sx={{ height: 36, width: 'auto', mr: 0.5, ml: { xs: 0, sm: 1 } }} />
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, letterSpacing: '0.04em' }}>
            HotQuus
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {/* Same top-bar station control pattern as dashboard StationSwitcher (#31) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Radio sx={{ color: onBar.icon }} />
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <Select
                value={selectedId === '' ? '' : selectedId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedId(v === '' ? '' : Number(v));
                }}
                displayEmpty
                disabled={stations.length === 0}
                renderValue={() => {
                  if (selectedId === '' || stations.length === 0) {
                    return (
                      <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                        {stations.length === 0 ? 'No stations available' : 'Select Station'}
                      </Typography>
                    );
                  }
                  const station = stations.find((s) => s.id === selectedId);
                  return station ? formatStationMenuLabel(station) : '';
                }}
                sx={{
                  color: onBar.label,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: onBar.border },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: onBar.borderHover },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: figma.cyan },
                  '& .MuiSvgIcon-root': { color: onBar.icon },
                }}
              >
                {stations.map((station) => (
                  <MenuItem key={station.id} value={station.id}>
                    {formatStationMenuLabel(station)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              disabled={selectedId === '' || stations.length === 0}
              onClick={handleContinue}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#ffffff',
                backgroundColor: '#4DAADB',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#409dcf', boxShadow: 'none' },
              }}
            >
              Continue
            </Button>

            <IconButton onClick={handleLogout} sx={{ color: topBarIcon }} aria-label="Log out">
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Typography variant="body1" sx={{ color: figma.textMuted, textAlign: 'center', maxWidth: 440 }}>
          {centerMessage}
        </Typography>
      </Box>
    </Box>
  );
};

export default StationSelection;
